import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, Mail, Clock, CheckCircle, Sparkles, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { demoApi } from '../services/api';

const DemoLandingPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [config, setConfig] = useState(null);
  const [email, setEmail] = useState('');

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await demoApi.getPublicConfig();
        setConfig(response.data);
      } catch (error) {
        console.error('Failed to fetch demo config:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, []);

  const handleStartDemo = async () => {
    setStarting(true);
    
    try {
      const response = await demoApi.startDemo({
        email: email || undefined
      });
      
      const { session_token, product_id, expires_at } = response.data;
      
      // Store demo session info
      localStorage.setItem('demo_session_token', session_token);
      localStorage.setItem('demo_expires_at', expires_at);
      localStorage.setItem('is_demo_session', 'true');
      
      // Set the session cookie (backend also sets httpOnly cookie)
      document.cookie = `session_token=${session_token}; path=/; max-age=${24 * 60 * 60}`;
      
      toast.success('Demo environment created! Redirecting...');
      
      // Redirect to the product
      setTimeout(() => {
        window.location.href = `/products/${product_id}`;
      }, 1000);
      
    } catch (error) {
      console.error('Failed to start demo:', error);
      const message = error.response?.data?.detail || 'Failed to start demo. Please try again.';
      toast.error(message);
      setStarting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!config?.demo_available) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center p-8">
          <h1 className="text-2xl font-bold text-white mb-4">Demo Not Available</h1>
          <p className="text-slate-400 mb-6">The demo experience is not currently available.</p>
          <Button onClick={() => navigate('/')} variant="outline">
            Return to Home
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="p-6 flex justify-between items-center max-w-6xl mx-auto">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <span className="text-xl font-bold text-white">StackRanked.ai</span>
        </div>
        <div className="flex gap-3">
          <Button variant="ghost" className="text-slate-300 hover:text-white" onClick={() => navigate('/login')}>
            Sign In
          </Button>
          <Button onClick={() => navigate('/signup')}>
            Get Started
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <main className="max-w-4xl mx-auto px-6 py-16 text-center">
        <div className="inline-flex items-center gap-2 bg-blue-600/20 text-blue-400 px-4 py-2 rounded-full text-sm font-medium mb-8">
          <Play className="w-4 h-4" />
          Interactive Demo
        </div>
        
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
          Experience {config.product_name || 'StackRanked'} 
          <span className="text-blue-500"> in Action</span>
        </h1>
        
        <p className="text-lg text-slate-400 mb-12 max-w-2xl mx-auto">
          {config.product_description || 
            'Explore our AI-powered product management platform with a fully populated demo environment. No signup required.'}
        </p>

        {/* Demo Card */}
        <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-8 max-w-lg mx-auto backdrop-blur-sm">
          <h2 className="text-xl font-semibold text-white mb-2">Start Your Demo</h2>
          <p className="text-slate-400 text-sm mb-6">
            Get instant access to a pre-populated demo environment
          </p>

          {/* Email Input (Optional) */}
          <div className="mb-6">
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
              <Input
                type="email"
                placeholder="Email (optional - for follow-up)"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-11 bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500"
              />
            </div>
            <p className="text-xs text-slate-500 mt-2 text-left">
              We&apos;ll only reach out if you want to learn more
            </p>
          </div>

          {/* Start Button */}
          <Button 
            onClick={handleStartDemo} 
            disabled={starting}
            className="w-full h-12 text-lg bg-blue-600 hover:bg-blue-700"
            data-testid="start-demo-btn"
          >
            {starting ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Creating your demo...
              </>
            ) : (
              <>
                <Play className="w-5 h-5 mr-2" />
                Start Demo
                <ArrowRight className="w-5 h-5 ml-2" />
              </>
            )}
          </Button>

          {/* Features List */}
          <div className="mt-8 space-y-3 text-left">
            <div className="flex items-center gap-3 text-slate-300">
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
              <span className="text-sm">Pre-populated with realistic product data</span>
            </div>
            <div className="flex items-center gap-3 text-slate-300">
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
              <span className="text-sm">Full access to all features - no restrictions</span>
            </div>
            <div className="flex items-center gap-3 text-slate-300">
              <Clock className="w-5 h-5 text-blue-500 flex-shrink-0" />
              <span className="text-sm">Demo expires in {config.demo_duration_hours || 24} hours</span>
            </div>
          </div>
        </div>

        {/* Trust Signals */}
        <div className="mt-12 flex items-center justify-center gap-8 text-slate-500 text-sm">
          <span>✓ No credit card required</span>
          <span>✓ No signup needed</span>
          <span>✓ Instant access</span>
        </div>
      </main>

      {/* Footer */}
      <footer className="mt-16 py-8 border-t border-slate-800">
        <div className="max-w-6xl mx-auto px-6 flex justify-between items-center">
          <p className="text-slate-500 text-sm">
            © 2026 StackRanked.ai - AI-Powered Product Management
          </p>
          <div className="flex gap-6">
            <a href="/terms" className="text-slate-500 hover:text-slate-300 text-sm">Terms</a>
            <a href="/privacy" className="text-slate-500 hover:text-slate-300 text-sm">Privacy</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DemoLandingPage;
